import { createAsyncThunk } from '@reduxjs/toolkit';

import { AdminApiPaths } from '../../../api/APIPaths';
import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { ICourseTemplate } from '.';

export type TCreateCourseTemplateAsAdmin = {
  course: string;
  name: string;
};

export type TGetCourseTemplatesAsAdmin = {
  searchText?: string;
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  courseId?: string;
  curriculum?: string;
  subject?: string;
  grade?: string;
};

export type TGetCourseTemplateAsAdmin = {
  id: string;
  isSectionsAllowed?: boolean;
};

export type TAdminUpdateCourseTemplate = {
  id: string;
  name?: string;
  isTrialTemplate?: boolean;
};

const apiDataProvider = new APIDataProvider();

const getCourseTemplatesAsAdminModule = async ({
  limit,
  offset,
  sortKey,
  sortValue,
  searchText,
  courseId,
  curriculum,
  subject,
  grade,
}: TGetCourseTemplatesAsAdmin) => {
  let path = `${AdminApiPaths.courseTemplates}?limit=${limit}&offset=${offset}`;

  if (sortKey) {
    path += `&sortKey=${sortKey}`;
  }

  if (sortValue) {
    path += `&sortValue=${sortValue}`;
  }

  if (searchText) {
    path += `&searchText=${searchText}`;
  }

  if (courseId) {
    path += `&courseId=${courseId}`;
  }

  if (curriculum) {
    path += `&curriculum=${curriculum}`;
  }

  if (subject) {
    path += `&subject=${subject}`;
  }

  if (grade) {
    path += `&grade=${grade}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<ICourseTemplate>>
  >(path);

  return response.data;
};

export const createCourseTemplateAsAdmin = createAsyncThunk(
  '/admin/course-templates/create',
  async (data: TCreateCourseTemplateAsAdmin) => {
    const path = `${AdminApiPaths.courseTemplates}`;
    const response = await apiDataProvider.post<TApiResponse<ICourseTemplate>>(
      path,
      data
    );

    return response.data;
  }
);

export const getCourseTemplateByIdAsAdmin = createAsyncThunk(
  '/admin/course-templates/id',
  async (data: TGetCourseTemplateAsAdmin) => {
    let path = `${AdminApiPaths.courseTemplates}/${data.id}`;

    if (data.isSectionsAllowed) {
      path += `?isSectionsAllowed=1`;
    }

    const response = await apiDataProvider.get<TApiResponse<ICourseTemplate>>(
      path
    );

    return response.data;
  }
);

export const adminUpdateCourseTemplate = createAsyncThunk(
  '/admin/course-templates/update-one',
  async (data: TAdminUpdateCourseTemplate) => {
    const path = `${AdminApiPaths.courseTemplates}/${data.id}`;

    const response = await apiDataProvider.put<TApiResponse<ICourseTemplate>>(
      path,
      data
    );

    return response.data;
  }
);

export const adminDeleteCourseTemplate = createAsyncThunk(
  '/admin/course-templates/delete-one',
  async (id: string) => {
    const path = `${AdminApiPaths.courseTemplates}/${id}`;

    const response = await apiDataProvider.delete<
      TApiResponse<ICourseTemplate>
    >(path);

    return response.data;
  }
);

export const getCourseTemplatesAsAdmin = createAsyncThunk(
  '/admin/course-templates/list',
  getCourseTemplatesAsAdminModule
);

export const loadMoreCourseTemplatesAsAdmin = createAsyncThunk(
  '/admin/course-templates/list/load-more',
  getCourseTemplatesAsAdminModule
);
